
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Send, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const OneSignalNotificationForm = () => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSendNotification = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Por favor completa título y mensaje');
      return;
    }

    setIsLoading(true);

    try {
      console.log('Enviando notificación:', { title, message });

      const { data, error } = await supabase.functions.invoke('onesignal-notifications', {
        body: {
          action: 'sendNotification',
          title: title.trim(),
          message: message.trim()
        }
      });

      if (error) {
        console.error('Error enviando notificación:', error);
        toast.error('Error enviando notificación: ' + error.message);
        return;
      }

      console.log('Respuesta del servidor:', data);

      if (data?.success) {
        // Mensaje mejorado que incluye información sobre las notificaciones guardadas
        const pushCount = data.sent || 0;
        const inAppCount = data.inAppNotifications || 0;
        
        if (pushCount === 0 && inAppCount > 0) {
          toast.success(`¡Notificación enviada! Procesándose... ${inAppCount} notificaciones guardadas en la app`);
        } else if (pushCount > 0 && inAppCount > 0) {
          toast.success(`¡Notificación enviada! ${pushCount} dispositivos alcanzados, ${inAppCount} notificaciones guardadas`);
        } else if (inAppCount > 0) {
          toast.success(`¡Notificación enviada! ${inAppCount} notificaciones guardadas en la app`);
        } else {
          toast.success('¡Notificación enviada correctamente!');
        }
        
        setTitle('');
        setMessage('');
      } else {
        toast.error('Error: ' + (data?.error || 'Respuesta inesperada'));
      }

    } catch (error) {
      console.error('Error:', error);
      toast.error('Error enviando notificación');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="text-vegana-primary" />
          Enviar Notificación Push
        </CardTitle>
        <CardDescription>
          Envía notificaciones push instantáneas y las guarda en la app para todos los usuarios
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="notification-title">Título</Label>
          <Input
            id="notification-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej: Consejo nutricional del día"
            maxLength={50}
          />
          <p className="text-xs text-gray-500">{title.length}/50 caracteres</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="notification-message">Mensaje</Label>
          <Textarea
            id="notification-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ej: Recuerda incluir proteína vegetal en cada comida para mantener tus músculos fuertes"
            rows={3}
            maxLength={200}
          />
          <p className="text-xs text-gray-500">{message.length}/200 caracteres</p>
        </div>

        <Button
          onClick={handleSendNotification}
          disabled={!title.trim() || !message.trim() || isLoading}
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Enviar Notificación
            </>
          )}
        </Button>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>Sistema de notificaciones</strong> envía mensajes push y los guarda automáticamente en la pestaña de notificaciones de cada usuario.
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Nota: Las notificaciones push pueden mostrar 0 inicialmente, pero se entregan correctamente. Las notificaciones siempre se guardan en la app.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default OneSignalNotificationForm;
